import useTranslationModels from "../../../hooks/useTranslationModels";
import TranslationModelSelect from "./TranslationModelSelect";
import { useSearchParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { axiosInstance } from "../../../config";
import {
  TranslationActivity,
  translationLanguages,
} from "./TranslationControls";
export default function TranslationCatalogue({ language, compact = false }) {
  const models = useTranslationModels();
  const [params, setParams] = useSearchParams();
  const update = (key, value) => setParams(previous => {
    const next = new URLSearchParams(previous);
    next.set(key, String(value));
    if (key === "coverageFilter" || key === "coverageSearch") next.set("coveragePage", "1");
    return next;
  }, { replace: true });
  const [open, setOpen] = useState(!compact);
  const knownFilters = ["remaining", "untranslated", "incomplete", "outdated", "translated", "blocked", "all"];
  const filter = knownFilters.includes(params.get("coverageFilter")) ? params.get("coverageFilter") : "remaining";
  const limitValue = params.get("coverageLimit");
  const limit = limitValue === "all" ? "all" : [10,20,50,100,200,300].includes(Number(limitValue)) ? Number(limitValue) : 10;
  const page = Math.max(1, Number.parseInt(params.get("coveragePage"), 10) || 1);
  const search = params.get("coverageSearch") || "";
  const setFilter = value => update("coverageFilter", value);
  const setLimit = value => update("coverageLimit", value);
  const setPage = value => update("coveragePage", value);
  const setSearch = value => update("coverageSearch", value);
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [version, setVersion] = useState(0);
  useEffect(() => {
    setData(null);
    setMessage("");
  }, [language]);
  useEffect(() => {
    if (!open || !language || language === "en") return;
    let live = true;
    const load = async () => {
      try {
        const res = await axiosInstance.get("/api/translations/catalogue", {
          params: { language, filter, page, search },
        });
        if (live) {
          setData(res.data);
          setError("");
        }
      } catch (e) {
        if (live)
          setError(
            e.response?.data?.message || "Cannot load translation coverage.",
          );
      }
    };
    const debounce = setTimeout(load, 200),
      timer = setInterval(load, 6000);
    return () => {
      live = false;
      clearTimeout(debounce);
      clearInterval(timer);
    };
  }, [language, filter, page, search, open, version]);
  if (!language || language === "en") return null;
  async function start() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await axiosInstance.post("/api/translations/batches", {
        language,
        selection: models.selection,
        limit,
        filter,
      });
      setMessage(
        `Queued ${res.data.total} medicines for ${translationLanguages[language]}. You can close this page; progress is saved on the AI translations page.`,
      );
      setVersion((v) => v + 1);
    } catch (e) {
      setError(
        e.response?.data?.message ||
          "The translation request was not accepted.",
      );
    } finally {
      setBusy(false);
    }
  }
  const canTranslate = ["remaining", "untranslated", "incomplete"].includes(
    filter,
  );
  return (
    <Accordion expanded={open} onChange={(_, v) => setOpen(v)} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>
          {translationLanguages[language]} translation coverage & bulk
          translation
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          English is the source. Existing translated fields are preserved. Bulk
          translation fills only gaps, newest English medicines first, one
          medicine at a time.
        </Typography>
        {data && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Not translated: {data.counts.untranslated} · Missing fields:{" "}
            {data.counts.incomplete} · Outdated AI translations:{" "}
            {data.counts.outdated} · Translated: {data.counts.translated} ·
            Relationship issues: {data.counts.blocked}
          </Typography>
        )}
        <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 0, width: { xs: "100%", sm: 220 } }}>
            <InputLabel>Translation status</InputLabel>
            <Select
              label="Translation status"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
              }}
            >
              {Object.entries({
                remaining: "All remaining medicines",
                untranslated: "Not translated",
                incomplete: "Missing translated fields",
                outdated: "Outdated AI translations",
                translated: "Translated",
                blocked: "Relationship issues",
                all: "All English medicines",
              }).map(([v, title]) => (
                <MenuItem key={v} value={v}>
                  {title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 0, width: { xs: "100%", sm: 190 } }}>
            <InputLabel>How many medicines</InputLabel>
            <Select
              label="How many medicines"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
            >
              {[10, 20, 50, 100, 200, 300, "all"].map((n) => (
                <MenuItem key={n} value={n}>
                  {n === "all"
                    ? "All matching medicines"
                    : `Latest ${n} medicines`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TranslationModelSelect models={models} disabled={busy} />
          <Button
            variant="contained"
            disabled={busy || !models.ready || !data?.total || !canTranslate}
            onClick={start}
          >
            Translate & publish remaining
          </Button>
          <TextField
            size="small"
            label="Find English medicine"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </Stack>
        {!canTranslate && (
          <Typography variant="caption">
            Select Not translated, Missing translated fields or All remaining
            medicines to start bulk translation.
          </Typography>
        )}
        {search && (
          <Typography variant="caption" display="block">
            Search filters the list below. Bulk translation uses the selected
            status and quantity across the catalogue.
          </Typography>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}
        {data && (
          <Box sx={{ overflowX: "auto", mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>English medicine (newest first)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Missing / outdated fields</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((row) => (
                  <TableRow key={row.sourceId}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>
                      {[...row.missingFields, ...row.staleFields].join(", ") ||
                        "—"}
                    </TableCell>
                  </TableRow>
                ))}
                {!data.rows.length && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      No medicines match this filter.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Pagination
              count={Math.max(1, Math.ceil(data.total / data.pageSize))}
              page={page}
              onChange={(_, p) => setPage(p)}
              sx={{ mt: 1 }}
            />
          </Box>
        )}
        <TranslationActivity compact />
      </AccordionDetails>
    </Accordion>
  );
}
